<template>
  <busy-area :busy="isLoading">
    <button type="button" class="link" @click.prevent.stop="clickExport">
      <export-icon class="flex-0 h-5.5" />
      <label class="flex-1">{{ label }}</label>
    </button>
  </busy-area>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import ExportIcon from '@images/export.svg?component';
import BusyArea from '../../busy/BusyArea.vue';
import { useFileExport } from '@/hooks/file';
import type { Filter } from '../../table/hooks/provider';

interface Props {
  url?: string;
  filter?: Filter;
  columns?: { key: string, title?: string, unit?: string, csvTranslations?: { [key: string]: string } }[];
  visibleColumns?: { [key: string]: boolean };
  customExport?: () => Promise<void>;
  preExportCheck?: () => Promise<boolean>;
  title?: string;
  label: string;
  sortKey?: string;
  sortDirection?: 'ASC' | 'DESC';
  format: 'csv' | 'xlsx';
}

const props = withDefaults(defineProps<Props>(), {
  format: 'csv',
});

const exportColumns = computed(() =>
  props.columns?.filter((column) => props.visibleColumns?.[column.key] === true).map((col) => ({
    key: col.key,
    title: col.unit ? `${col.title} (${col.unit})` : col.title,
    translations: col.csvTranslations ?? {},
  })) ?? []);

const [startExport, loading] = useFileExport(
  props.url ?? '',
  computed(() => props.filter ?? {}),
  exportColumns.value,
  computed(() => props.title),
  computed(() => (props.sortKey && props.sortDirection ? { sortKey: props.sortKey, sortDirection: props.sortDirection } : undefined)),
  props.format,
);
const customLoading = ref(false);
const isLoading = computed(() => loading.value || customLoading.value);

const clickExport = async () => {
  const exportCheckResult = await props.preExportCheck?.();

  if (exportCheckResult === false) {
    return;
  }

  if (exportCheckResult) {
    startExport();
  } else if (props.customExport) {
    customLoading.value = true;
    try {
      await props.customExport();
    } finally {
      customLoading.value = false;
    }
  } else {
    startExport();
  }
};
</script>

<style lang="scss" scoped>
.link {
  @apply flex gap-x-3.5 items-center;
  @apply text-left text-body-regular font-normal;
  @apply cursor-pointer;

  max-width: px(350);

  label {
    @apply cursor-pointer;
  }
}
</style>
